import { isEmpty } from 'ramda';
import type { Path } from 'slate';
import { Editor, Element, Node, Range, Text, Transforms } from 'slate';

import { linkColor } from '../../constants';
import type {
  InsertLinkEdit,
  LinkElement,
  LinkProps,
  LinkURLEdit,
  RemoveLinkEdit,
} from '../internal';
import { AlineTransforms, EditingMode, isLinkElement, linkType, Queries } from '../internal';

interface NewLinkProps {
  href: string;
  modifiedLinkText?: string;
}

const LinkTransforms = {
  /**
   * Inserts a link at the provided Range, taking into account editing mode.
   */
  insertLink: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode; newLinkProps: NewLinkProps; at?: Range }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, newLinkProps } = options;
      const { href, modifiedLinkText } = newLinkProps;
      if (!at || Queries.rangeIncludesCheckboxElement(editor, at)) return;

      /**
       * First, we resolve the link selection since the selection may encompass inlines.
       * This acts to change the selection to the correct place if there are inlines present.
       */
      const resolvedLinkRange = AlineTransforms.nudgeRangeToEdges(editor, {
        at,
        match: Queries.linkNudgeInlineMatch(editor),
      });
      const rangeRef = Editor.rangeRef(editor, resolvedLinkRange, { affinity: 'inward' });
      if (!rangeRef.current) return;

      if (Range.isCollapsed(rangeRef.current)) {
        const { anchor } = rangeRef.current;
        const formats = Queries.formatsAtCursor(editor, { at: anchor });
        if (!formats) return;

        const collapsedLink: LinkElement = {
          children: [
            {
              comments: [],
              text: modifiedLinkText ? modifiedLinkText : href,
              ...formats,
              fontColor: {
                color: linkColor,
                origin: 'USER_APPLIED',
              },
              underline: {
                active: true,
                origin: 'USER_APPLIED',
              },
            },
          ],
          href,
          suggestedEdits: [],
          type: linkType,
        };

        AlineTransforms.insertContentNodes(editor, {
          at: anchor,
          authorId,
          editingMode,
          nodes: [collapsedLink],
          processEdits: false,
        });

        rangeRef.unref();

        return;
      }

      /**
       * rangeRef.current is an expanded Range.
       * If the link text is modified, we insert the link with the provided children at the Range using the formats from the first text node in the Range.
       */
      if (modifiedLinkText) {
        const formatsForModifiedText = Queries.getFirstTextFormats(editor, {
          at: rangeRef.current,
        });

        if (!formatsForModifiedText) return;

        const modifiedLink: LinkElement = {
          children: [
            {
              comments: [],
              text: modifiedLinkText,
              ...formatsForModifiedText,
              fontColor: {
                color: linkColor,
                origin: 'USER_APPLIED',
              },
              underline: {
                active: true,
                origin: 'USER_APPLIED',
              },
            },
          ],
          href,
          suggestedEdits: [],
          type: linkType,
        };

        /**
         * AlineTransforms.insertContentNodes, when inserting using a Range, first deletes the entire range and then inserts the node(s).
         * This means we don't have to worry about links wrapping text in different paragraphs.
         */
        AlineTransforms.insertContentNodes(editor, {
          at: rangeRef.current,
          authorId,
          editingMode,
          nodes: [modifiedLink],
          processEdits: false,
        });

        rangeRef.unref();

        return;
      }

      const rangeHasInlines = Queries.locationHasInlines(editor, {
        at: rangeRef.current,
        voids: true,
      });

      /**
       * If there are any inlines in the Range, we unwrap them so that inlines don't wrap other inlines.
       * ToDo:  This is not entirely correct for tracked changes.  If tracking changes, the user is really suggesting to replace the link URL.  Fix later.
       */
      if (rangeHasInlines)
        Transforms.unwrapNodes(editor, {
          at: rangeRef.current,
          match: node => Element.isElement(node) && Editor.isInline(editor, node),
          split: true,
          voids: true,
        });

      const insertLinkEdit: InsertLinkEdit = {
        authorId,
        type: 'insertLink',
        value: { href },
      };
      const unmodifiedLink: LinkElement = {
        children: [],
        href,
        suggestedEdits: editingMode === EditingMode.SUGGESTING ? [insertLinkEdit] : [],
        type: linkType,
      };

      AlineTransforms.splitTextAtEdges(editor, {
        at: rangeRef.current,
        editingMode,
        hanging: true,
        voids: true,
      });
      Transforms.wrapNodes(editor, unmodifiedLink, {
        at: rangeRef.current,
        match: Text.isText,
      });
      Transforms.setNodes(
        editor,
        {
          fontColor: { color: linkColor, origin: 'USER_APPLIED' },
          underline: { active: true, origin: 'USER_APPLIED' },
        },
        { at: rangeRef.current, match: Text.isText }
      );

      const linkRange = rangeRef.unref();
      if (linkRange) {
        // Place the cursor at the end of the range after link insertion.
        AlineTransforms.select(editor, Editor.end(editor, linkRange));
      }
    });
  },

  /**
   * Remove a link from the document, taking into account editing mode, etc.
   */
  removeLink: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      linkPath: Path;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, linkPath } = options;
      const existingLink = Node.get(editor, linkPath);
      if (!isLinkElement(existingLink)) return;

      if (editingMode === EditingMode.SUGGESTING) {
        const removeLinkEdit: RemoveLinkEdit = {
          authorId,
          removedHref: existingLink.href,
          type: 'removeLink',
          value: {},
        };

        const newSuggestedEdits = Queries.newEdits(existingLink, removeLinkEdit);

        if (!newSuggestedEdits) return;

        Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: linkPath });

        // Also update the text formatting inside the link so it appears as normal text.
        const formats = Queries.removeLinkFormats(editor, linkPath);

        Transforms.setNodes(editor, formats, {
          at: linkPath,
          match: Text.isText,
          split: true,
        });

        return;
      }

      AlineTransforms.unwrapLink(editor, { editingMode, linkPath });
    });
  },

  /**
   * Unwrap link from a piece of text but leave the underling text.
   */
  unwrapLink: (editor: Editor, options: { editingMode: EditingMode; linkPath: Path }): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { linkPath } = options;
      const formats = Queries.removeLinkFormats(editor, linkPath);

      Transforms.setNodes(editor, formats, {
        at: linkPath,
        match: Text.isText,
        split: true,
      });
      Transforms.unwrapNodes(editor, { at: linkPath });
    });
  },
  /**
   * Update a link's text or URL, taking into account editing mode.
   */
  updateLink: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      linkPath: Path;
      modifiedLinkText?: string;
      newHref?: string;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, linkPath, modifiedLinkText, newHref } = options;

      if (!newHref && modifiedLinkText === undefined) {
        console.error('You must pass a modified href or link text to AlineTransforms.updateLink.');
        return;
      }

      if (newHref)
        AlineTransforms.updateLinkURL(editor, {
          authorId,
          editingMode,
          linkPath,
          newHref,
          select: !modifiedLinkText,
        });

      /**
       * We intentionally want to permit an empty string in this function since a user can suggest an empty string
       * for text, in which case we make the URL the text of the link.
       */
      if (typeof modifiedLinkText === 'string')
        AlineTransforms.updateLinkText(editor, {
          authorId,
          editingMode,
          linkPath,
          newText: modifiedLinkText,
          select: true,
        });
    });
  },
  /**
   * Updates the text of a link, taking into account editing mode, etc.
   */
  updateLinkText: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      linkPath: Path;
      newText: string;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, linkPath, newText, select = false } = options;

      /**
       * The user may have edited the link text to an empty string, in which case we set the text of the link
       * in the editor to the URL.
       */
      if (isEmpty(newText)) {
        const existingLink = Node.get(editor, linkPath);

        if (!isLinkElement(existingLink)) return;

        AlineTransforms.insertText(editor, {
          at: linkPath,
          authorId,
          editingMode,
          textToInsert: existingLink.href,
        });

        if (select) {
          AlineTransforms.select(editor, Editor.end(editor, linkPath));
        }

        return;
      }

      AlineTransforms.insertText(editor, {
        at: linkPath,
        authorId,
        editingMode,
        textToInsert: newText,
      });

      if (select) {
        AlineTransforms.select(editor, Editor.end(editor, linkPath));
      }
    });
  },
  /**
   * Updates the URL of a link, taking into account editing mode, etc.
   */
  updateLinkURL: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      linkPath: Path;
      newHref: string;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, linkPath, newHref, select = false } = options;
      const link = Node.get(editor, linkPath);
      if (!isLinkElement(link)) return;

      const endLink = Editor.end(editor, linkPath);
      const pointRef = Editor.pointRef(editor, endLink);
      const linkProps: Partial<LinkProps> = { href: newHref };

      if (editingMode === EditingMode.SUGGESTING) {
        const linkEdit: LinkURLEdit = {
          authorId,
          oldHref: link.href,
          type: 'linkEdit',
          value: { href: newHref },
        };

        const newSuggestedEdits = Queries.newEdits(link, linkEdit);
        if (!newSuggestedEdits) return;

        Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: linkPath });

        const point = pointRef.unref();
        if (point && select) AlineTransforms.select(editor, point);

        return;
      }

      AlineTransforms.removeConflictingSuggestions(editor, {
        editingMode,
        entry: [link, linkPath],
        props: linkProps,
      });
      Transforms.setNodes(editor, linkProps, {
        at: linkPath,
      });

      const point = pointRef.unref();
      if (point && select) AlineTransforms.select(editor, point);
    });
  },
};

export default LinkTransforms;
